import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";
import {DatabaseCode, toDatabaseCode} from "./database-code";

export const RateModelValidator = t.intersection([
  t.type({
    id: t.string,
    name: t.string,
    type: t.string,
    saveCostToTicket: t.boolean,
    regionId: t.string,
  }),
  t.partial({
    companyId: t.string,
    /** The kind of company the rate model bills, e.g. "Truck Company" for hauling. */
    companyType: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

/** Validator for an array of rate models. */
export const RateModelArrayValidator = t.array(RateModelValidator);

export type RateModel = t.TypeOf<typeof RateModelValidator>;
export type RateModelArray = t.TypeOf<typeof RateModelArrayValidator>;

export interface RateModelType extends DatabaseCode {
}

const transport: RateModelType = {
  code: 'Transport',
  displayValue: {
    id: 'common.entity.ratemodel.type.transport',
    defaultMessage: 'Transport',
  },
};

const disposal: RateModelType = {
  code: 'Disposal',
  displayValue: {
    id: 'common.entity.ratemodel.type.disposal',
    defaultMessage: 'Disposal',
  },
};

const subcontractor: RateModelType = {
  code: 'Subcontractor',
  displayValue: {
    id: 'common.entity.ratemodel.type.subcontractor',
    defaultMessage: 'Subcontractor',
  },
};

export const unknownRateModelType: Readonly<RateModelType> =
  Object.freeze<RateModelType>({
    code: 'UNKNOWN',
    displayValue: {
      id: 'common.shared.unknown',
      defaultMessage: 'Unknown',
    },
  });

export const rateModelType = {
  transport,
  disposal,
  subcontractor,
};

export const rateModelTypes = Object.values(rateModelType);

export const toRateModelType = (code: string): RateModelType => toDatabaseCode(code, rateModelTypes, unknownRateModelType);

/**
 * An rate model entity.
 */
@Exclude()
export class RateModelEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  name!: string;

  @Transform(getDatabaseCodeTransformer(toRateModelType, unknownRateModelType))
  @Expose()
  type!: RateModelType;

  @Expose()
  saveCostToTicket = true;

  @Expose()
  regionId!: string;

  @Expose()
  companyId?: string;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  readonly entityType = 'RATEMODEL';

  get displayName(): Readonly<string> {
    return this.name;
  }

  /**
   * Constructor for RateModelEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create a RateModelEntity from a wel type value.
   *
   * @param rateModel the rate model type value.
   */
  static from(rateModel: RateModel): RateModelEntity {
    return plainToInstance(RateModelEntity, rateModel, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<RateModel> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(RateModelValidator, object);
  }

  asTypeValue(): RateModel {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(RateModelValidator, object);
  }
}

export interface RateModelSearchCriteria {
  ids?: string[]
  regionIds?: string[];
  companyIds?: string[];
  types?: string[]
}
